import { mergeAttributes, Node, nodeInputRule } from '@tiptap/core';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import CustomImage from '@/components/composer/CustomImage.vue';
import { normalizeWebUrl } from '@/components/composer/utils/normalizeWebUrl.ts';

const inputRegex = /(?:^|\s)(!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\))$/;

const parseDimension = (value) => {
    if (value == null || value === '') {
        return null;
    }

    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
};

const parseDimensionFromStyle = (style, property) => {
    if (!style) {
        return null;
    }

    const regex = new RegExp(`(?:^|;)\\s*${property}\\s*:\\s*([0-9.]+)px(?:\\s*;|$)`, 'i');
    const match = style.match(regex);

    return parseDimension(match?.[1]);
};
/**
 * Node Object from the @tiptap/core.
 * @see https://tiptap.dev/docs/editor/extensions/custom-extensions/create-new/node#node-options
 */
export default Node.create({
    name: 'image',
    draggable: true,

    addOptions() {
        return {
            inline: false,
            allowBase64: false,
            HTMLAttributes: {},
        };
    },

    inline() {
        return this.options.inline;
    },

    group() {
        return this.options.inline ? 'inline' : 'block';
    },

    addAttributes() {
        return {
            src: {
                default: null,
            },
            alt: {
                default: null,
            },
            title: {
                default: null,
            },
            width: {
                default: null,
            },
            height: {
                default: null,
            },
            href: {
                default: null,
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: this.options.allowBase64 ? 'img[src]' : 'img[src]:not([src^="data:"])',
                getAttrs: (dom) => {
                    const style = dom.getAttribute('style');
                    const width =
                        parseDimension(dom.getAttribute('width')) ||
                        parseDimensionFromStyle(style, 'width');
                    const height =
                        parseDimension(dom.getAttribute('height')) ||
                        parseDimensionFromStyle(style, 'height');

                    return {
                        src: dom.getAttribute('src'),
                        title: dom.getAttribute('title'),
                        alt: dom.getAttribute('alt'),
                        width,
                        height,
                        href: normalizeWebUrl(dom.closest('a[href]')?.getAttribute('href') ?? ''),
                    };
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        const { href, ...imageAttributes } = HTMLAttributes;
        const image = ['img', mergeAttributes(this.options.HTMLAttributes, imageAttributes)];

        if (!href) {
            return image;
        }

        return [
            'a',
            {
                href,
                target: '_blank',
                rel: 'noopener noreferrer',
            },
            image,
        ];
    },

    addCommands() {
        return {
            setImage:
                (options) =>
                ({ commands }) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: options,
                    });
                },
        };
    },

    addInputRules() {
        return [
            nodeInputRule({
                find: inputRegex,
                type: this.type,
                getAttributes: (match) => {
                    const [, , alt, src, title, width, height] = match;
                    return { src, alt, title, width, height };
                },
            }),
        ];
    },

    addNodeView() {
        return VueNodeViewRenderer(CustomImage);
    },
});
